import { MutationTree } from "vuex/types";

import { FaqState } from "./types";
import Vue from "vue";
import store from "@/store";
import _ from "lodash";
import { updateApolloDataset } from "@/helperMethods/faq/util";

/**
 * Mutations for the FAQ module.
 * Handles synchronous state updates for FAQ training and topic management.
 */
const mutations: MutationTree<FaqState> = {
  /**
   * Adds a new intent to the beginning of the training dataset.
   *
   * @param {FaqState} state - Module state
   * @param {Object} intent - Intent object to add
   */
  SET_ADD_NEW_INTENT(state, intent) {
    const clonedDataset = _.get(store, "state.training.dataset", []);
    clonedDataset.unshift(intent);
    Vue.set(store, "state.training.dataset", clonedDataset);
  },

  /**
   * Replaces the entire training dataset.
   *
   * @param {FaqState} state - Module state
   * @param {Array} dataset - New training dataset
   */
  SET_UPDATED_DATASET(state, dataset) {
    Vue.set(store, "state.training.dataset", dataset);
  },

  /**
   * Sets the active tab in the FAQ UI.
   *
   * @param {FaqState} state - Module state
   * @param {string} tabIndex - Tab identifier ('trainingData' or 'conversationalData')
   */
  SET_ACTIVE_TAB_INDEX(state, tabIndex) {
    // 1 = FAQ training dataset
    // 2 = FAQ conversational dataset
    Vue.set(state, "activeTab", tabIndex);
  },

  /**
   * Sets the training in progress flag.
   *
   * @param {FaqState} state - Module state
   * @param {boolean} inProgress - Whether training is in progress
   */
  SET_TRAINING_IN_PROGRESS(state, inProgress) {
    Vue.set(state, "trainingInProgress", inProgress);
  },

  /**
   * Sets the publishing in progress flag.
   *
   * @param {FaqState} state - Module state
   * @param {boolean} inProgress - Whether publishing is in progress
   */
  SET_PUBLISHING_IN_PROGRESS(state, inProgress) {
    Vue.set(state, "publishingInProgress", inProgress);
  },

  /**
   * Sets the currently viewing topic.
   *
   * @param {FaqState} state - Module state
   * @param {string} topic - Topic name
   */
  SET_CURRENT_VIEWING_TOPIC(state, topic) {
    Vue.set(state, "currentViewingTopic", topic);
  },

  /**
   * Sets the visibility of the topic selection dialog.
   *
   * @param {FaqState} state - Module state
   * @param {boolean} isVisible - Dialog visibility state
   */
  SET_TOPIC_DIALOG_VISIBLE(state, isVisible) {
    Vue.set(state, "selectTrainTopicsDialogVisible", isVisible);
  },

  /**
   * Sets the search value for filtering.
   *
   * @param {FaqState} state - Module state
   * @param {string} newSearchValue - New search query
   */
  SET_SEARCH_VALUE(state, newSearchValue) {
    Vue.set(state, "search", newSearchValue);
  },

  /**
   * Sets the selected topics for training.
   *
   * @param {FaqState} state - Module state
   * @param {Array<string>} selectedTopics - Array of selected topic names
   */
  SET_SELECTED_TOPICS(state, selectedTopics) {
    // hp: should set to selected topics instead of topics.
    Vue.set(state, "selectedTopics", selectedTopics);
  },

  /**
   * Sets the complete list of available topics.
   *
   * @param {FaqState} state - Module state
   * @param {Array<string>} newTopics - Array of topic names
   */
  SET_TOPICS(state, newTopics) {
    Vue.set(state, "topics", newTopics);
  },

  /**
   * Updates a single intent with create/modify/delete actions.
   * Also updates Apollo cache for GraphQL queries.
   *
   * @param {FaqState} state - Module state
   * @param {Object} payload - Mutation payload
   * @param {Object} payload.updatedIntent - Intent object to update
   * @param {string} payload.action - Action type ('CREATE', 'MODIFY', or 'DELETE')
   */
  SET_UPDATED_INTENT(state, { updatedIntent, action }) {
    const isCreate = action === "CREATE";
    const isDelete = action === "DELETE";
    const isModify = action === "MODIFY";
    const trainingDataset = store.state.training.dataset;
    const newTrainingDataset = _.cloneDeep(trainingDataset);

    if (isCreate) {
      const intentIndex = _.findIndex(trainingDataset, {
        id: updatedIntent.id,
      });
      const intentFound = intentIndex !== -1;
      if (intentFound) {
        return;
      }
      newTrainingDataset.push(updatedIntent);
    }

    const intentIndex = _.findIndex(trainingDataset, {
      id: updatedIntent.id,
    });
    const intentNotFound = intentIndex === -1;

    if (intentNotFound) {
      return;
    }

    if (isModify) {
      newTrainingDataset[intentIndex] = updatedIntent;
    } else if (isDelete) {
      newTrainingDataset.splice(intentIndex, 1);
    }

    Vue.set(store.state.training, "dataset", newTrainingDataset);

    // Updates all dataset query
    updateApolloDataset(newTrainingDataset);
  },
};

export default mutations;
